/**
 * The Kessmouth week (WP-X5, docs/WEEK.md): one modifier a week and honours that are kept for ever. Shared by the
 * server (the only thing that applies a modifier to a price, a police unit or an event, and the only thing that
 * writes an honour down), the balance models (they run every modifier against the documented ceilings) and the
 * client (the book's "The week" page, the radio).
 *
 * The week is the markets' contract week (Monday 00:00 UTC, `weekIndex` in shared/markets.ts). Which modifier a
 * week gets is a pure function of its index: a shuffled cycle of the five, never the same two weeks running, so
 * every server (and every restart) agrees without asking anybody. The server writes the week's modifier down when
 * the week starts (`week_ledger`) and from then on goes by that row.
 *
 * Bounded by construction (`WEEK_BOUNDS`, asserted per modifier by test): a modifier moves a price by at most a
 * tenth, adds a little police attention, multiplies influence in ONE district, makes one kind of event commoner or
 * rarer, or shortens the police's sight a little. It never touches health, damage, speed or any validation limit,
 * and it never pays anybody: honours are paid in a little gang reputation only. No seasons, no resets.
 */
import { DISTRICT_IDS, type BuyerType, type EconDistrictId, type QualityTier } from './economy';
import { GRADE_NAMES } from './markets';
import type { WorldEventKind } from './events';

// ----------------------------------------------------------------------------- modifiers

export const WEEK_MODIFIER_IDS = ['dock-strike', 'flying-squad', 'heatwave', 'fog', 'fancy'] as const;
export type WeekModifierId = (typeof WEEK_MODIFIER_IDS)[number];

/** A week's modifier with its parameters (the district the Flying Squad works, the grade the buyers fancy). */
export interface WeekModifier {
  id: WeekModifierId;
  week: number;
  district?: EconDistrictId;
  grade?: QualityTier;
}

/** Where a price is paid: the district buyers (street, fence), the big contested buyers, the temporary ones. */
export type WeekBuyerKind = BuyerType | 'big' | 'temp';

/** What a modifier changes. Everything optional; absent = no change. */
export interface WeekEffects {
  /** Price multiplier for these goods at the buyers listed (`kinds`; omitted: every buyer). */
  goods?: { defs: readonly string[]; mul: number; kinds?: readonly WeekBuyerKind[] };
  /** Price multiplier for one grade at the big buyers. */
  grade?: { tier: QualityTier; mul: number };
  /** Police attention (0..1) added in one district, or everywhere (`district: null`). */
  attention?: { district: EconDistrictId | null; add: number };
  /** Influence gained in one district is multiplied by this (never a fight: that moves influence, it doesn't make it). */
  influence?: { district: EconDistrictId; mul: number };
  /** World-event kinds' weights (0: none of that kind this week). */
  events?: Partial<Record<WorldEventKind, number>>;
  /** The police's sight range online (cars and officers; never the "lying low" range) is multiplied by this. */
  sight?: number;
}

export const NO_WEEK: WeekEffects = Object.freeze({});

/**
 * The most any modifier may do (asserted for every modifier and parameter by test). The price ceiling is small on
 * purpose: the late-game warehouse already sells within a fifth of the 1.35× ceiling (docs/ECONOMY.md §6).
 */
export const WEEK_BOUNDS = {
  priceMulMax: 1.1,
  attentionAddMax: 0.25,
  influenceMulMax: 1.5,
  sightMin: 0.8,
  eventWeightMax: 2,
} as const;

export const WEEK_TUNING = {
  /** Goods the boats bring in: short while the dockers are out. */
  strikeGoods: ['hooky-cigs', 'tax-discs'] as readonly string[],
  strikeMul: 1.1,
  squadAttention: 0.2,
  squadInfluence: 1.5,
  /** Thirsty weather: drink pays more at the pubs' and the corner's buyers (street and fences, not the big or temporary buyers). */
  heatwaveGoods: ['cut-spirits'] as readonly string[],
  heatwaveMul: 1.1,
  heatwaveAttention: 0.05,
  heatwaveRareCars: 1.5,
  fogSight: 0.8,
  fogBuyers: 1.5,
  fancyMul: 1.1,
} as const;

export function weekEffects(m: WeekModifier | null | undefined): WeekEffects {
  if (!m) return NO_WEEK;
  const T = WEEK_TUNING;
  switch (m.id) {
    case 'dock-strike':
      return { goods: { defs: T.strikeGoods, mul: T.strikeMul }, events: { shipment: 0 } };
    case 'flying-squad': {
      const d = m.district ?? DISTRICT_IDS[0];
      return { attention: { district: d, add: T.squadAttention }, influence: { district: d, mul: T.squadInfluence } };
    }
    case 'heatwave':
      return { goods: { defs: T.heatwaveGoods, mul: T.heatwaveMul, kinds: ['street', 'fence'] }, attention: { district: null, add: T.heatwaveAttention }, events: { rarecar: T.heatwaveRareCars } };
    case 'fog':
      return { sight: T.fogSight, events: { buyer: T.fogBuyers } };
    case 'fancy':
      return { grade: { tier: m.grade ?? 2, mul: T.fancyMul } };
  }
}

/** Price multiplier a modifier puts on a sale (1: none). */
export function weekPriceMul(e: WeekEffects, kind: WeekBuyerKind, defId: string, tier: QualityTier): number {
  let mul = 1;
  if (e.goods && e.goods.defs.includes(defId) && (!e.goods.kinds || e.goods.kinds.includes(kind))) mul *= e.goods.mul;
  if (e.grade && kind === 'big' && e.grade.tier === tier) mul *= e.grade.mul;
  return Math.max(0, Math.min(WEEK_BOUNDS.priceMulMax, mul));
}

/** Police attention a modifier adds in a district (0: none). */
export function weekAttention(e: WeekEffects, district: string): number {
  const a = e.attention;
  if (!a || (a.district !== null && a.district !== district)) return 0;
  return Math.max(0, Math.min(WEEK_BOUNDS.attentionAddMax, a.add));
}

/** Influence multiplier in a district (1: none). */
export function weekInfluenceMul(e: WeekEffects, district: string): number {
  const i = e.influence;
  return i && i.district === district ? Math.max(1, Math.min(WEEK_BOUNDS.influenceMulMax, i.mul)) : 1;
}

/** An event kind's weight multiplier (1: none; 0: not this week). */
export function weekEventWeight(e: WeekEffects, kind: WorldEventKind): number {
  const w = e.events?.[kind];
  return w === undefined ? 1 : Math.max(0, Math.min(WEEK_BOUNDS.eventWeightMax, w));
}

/** Police sight multiplier (1: none). */
export function weekSight(e: WeekEffects): number {
  return e.sight === undefined ? 1 : Math.max(WEEK_BOUNDS.sightMin, Math.min(1, e.sight));
}

// ----------------------------------------------------------------------------- which week gets which

/** A 32-bit integer hash (deterministic on every machine). */
function hash32(a: number, b = 0): number {
  let h = (Math.imul(a | 0, 0x9e3779b1) ^ Math.imul(b | 0, 0x85ebca77)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

/** The five modifiers in a shuffled order for a block of five weeks (Fisher–Yates on the block's hash). */
function blockOrder(block: number): number[] {
  const n = WEEK_MODIFIER_IDS.length;
  const order = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) {
    const j = hash32(block, i) % (i + 1);
    [order[i], order[j]] = [order[j]!, order[i]!];
  }
  return order;
}

/**
 * The modifier of a week: every modifier once in each block of five weeks, in a shuffled order, and never the same
 * one two weeks running (the first of a block is swapped away from the last of the block before — only positions
 * 0 and 1 ever swap, so the last of a block never depends on the block before it: no recursion).
 */
export function weekModifier(week: number): WeekModifier {
  const n = WEEK_MODIFIER_IDS.length;
  const block = Math.floor(week / n);
  const at = week - block * n;
  const order = blockOrder(block);
  const prevLast = blockOrder(block - 1)[n - 1]!;
  if (order[0] === prevLast) [order[0], order[1]] = [order[1]!, order[0]!];
  const id = WEEK_MODIFIER_IDS[order[at]!]!;
  const m: WeekModifier = { id, week };
  if (id === 'flying-squad') m.district = DISTRICT_IDS[hash32(week, 101) % DISTRICT_IDS.length]!;
  if (id === 'fancy') m.grade = (1 + (hash32(week, 202) % 2)) as QualityTier;
  return m;
}

/** A stored modifier row back into a modifier (unknown ids: none — a catalogue change never breaks an old week). */
export function parseModifier(week: number, id: string, params: string): WeekModifier | null {
  if (!(WEEK_MODIFIER_IDS as readonly string[]).includes(id)) return null;
  const m: WeekModifier = { id: id as WeekModifierId, week };
  try {
    const p = JSON.parse(params) as { district?: unknown; grade?: unknown };
    if (typeof p.district === 'string' && (DISTRICT_IDS as readonly string[]).includes(p.district)) m.district = p.district as EconDistrictId;
    if (p.grade === 0 || p.grade === 1 || p.grade === 2) m.grade = p.grade;
  } catch {
    /* no parameters */
  }
  return m;
}

export const modifierParams = (m: WeekModifier): string => JSON.stringify({ ...(m.district ? { district: m.district } : {}), ...(m.grade !== undefined ? { grade: m.grade } : {}) });

// ----------------------------------------------------------------------------- words

const DISTRICT_WORDS: Record<EconDistrictId, string> = {
  'cinder-yards': 'Cinder Yards',
  'the-parade': 'the Parade',
  'tanners-row': "Tanner's Row",
  'mercy-square': 'Mercy Square',
  'gullet-basin': 'Gullet Basin',
};
export const weekDistrictName = (d: string): string => DISTRICT_WORDS[d as EconDistrictId] ?? d;

/** The modifier's name as the Evening Post prints it. */
export function modifierName(m: WeekModifier): string {
  switch (m.id) {
    case 'dock-strike':
      return 'The dockers’ strike';
    case 'flying-squad':
      return `The Flying Squad in ${weekDistrictName(m.district ?? DISTRICT_IDS[0])}`;
    case 'heatwave':
      return 'The heatwave';
    case 'fog':
      return 'The fog';
    case 'fancy':
      return `A fancy for ${GRADE_NAMES[m.grade ?? 2]} goods`;
  }
}

/** What it means for you, in plain words (the book's page, the announcement). One line per change. */
export function modifierLines(m: WeekModifier): string[] {
  const pct = (mul: number): string => `${Math.round((mul - 1) * 100)} %`;
  const T = WEEK_TUNING;
  switch (m.id) {
    case 'dock-strike':
      return ['The dockers are out: no boats unload at the Basin this week.', `Cigarettes and tax discs are short: every buyer pays ${pct(T.strikeMul)} more for them.`];
    case 'flying-squad': {
      const d = weekDistrictName(m.district ?? DISTRICT_IDS[0]);
      return [`Detectives are working ${d} all week: more police about there, so sales there pay a little less and draw more heat.`, `Business done in ${d} counts half as much again towards running it.`];
    }
    case 'heatwave':
      return [`Everybody’s thirsty: street buyers and fences pay ${pct(T.heatwaveMul)} more for cut-price spirits.`, 'Tempers are short: a little more police attention everywhere.', 'Motors left out with the windows down: a rare motor turns up more often.'];
    case 'fog':
      return ['Thick fog off the river all week: the police can’t see as far (a fifth less).', 'Buyers from out of town come in under cover of it: they turn up more often.'];
    case 'fancy':
      return [`The big buyers are after ${GRADE_NAMES[m.grade ?? 2]} goods this week: they pay ${pct(T.fancyMul)} more for that grade.`];
  }
}

/** One short line for the radio's newsreader (no names: the modifier and a district only). */
export function modifierBulletin(m: WeekModifier): string {
  switch (m.id) {
    case 'dock-strike':
      return 'The dockers have walked out. No boats will be unloaded at the Basin this week, and the shops are already short of cigarettes.';
    case 'flying-squad':
      return `The Flying Squad are in ${weekDistrictName(m.district ?? DISTRICT_IDS[0])} this week. Residents can expect plain-clothes officers on every corner.`;
    case 'heatwave':
      return 'The heatwave goes on: the hottest week in years, and the pubs are running dry.';
    case 'fog':
      return 'Fog off the river all week. Drivers are asked to take care: visibility is poor all over the borough.';
    case 'fancy':
      return `Talk on the markets is all of ${GRADE_NAMES[m.grade ?? 2].toLowerCase()} goods this week, and the big buyers are paying for them.`;
  }
}

// ----------------------------------------------------------------------------- honours

/** What a firm's week is tallied in (`week_tallies.stat`). */
export const WEEK_STATS = ['crates', 'exports', 'sold-0', 'sold-1', 'sold-2', 'contests', 'jobs', 'value'] as const;
export type WeekStat = (typeof WEEK_STATS)[number];

export const WEEK = {
  /** Gang reputation for each honour a gang takes (through `recordProgress`, idempotent, under the daily cap). */
  honourRep: 20,
  /** Weeks of honours on the book's page. */
  pageWeeks: 6,
  /** Tallies are kept this many weeks after the week they count (the honours are kept for ever). */
  keepTallyWeeks: 5,
  /** The ledger looks at the clock this often. */
  sweepMs: 10_000,
} as const;

/** The honours, in the order the book prints them. `lead:<district>` is one per district. */
export type HonourId = `lead:${EconDistrictId}` | 'crates' | 'exports' | 'sold-0' | 'sold-1' | 'sold-2' | 'contests' | 'jobs' | 'independent';

export const HONOUR_IDS: readonly HonourId[] = [...DISTRICT_IDS.map((d) => `lead:${d}` as const), 'crates', 'exports', 'sold-2', 'sold-1', 'sold-0', 'contests', 'jobs', 'independent'];

/** The honour's title in the book, and what its figure counts. */
export function honourTitle(id: string): { title: string; unit: string } {
  if (id.startsWith('lead:')) return { title: `Ran ${weekDistrictName(id.slice(5))} at the week’s end`, unit: '' };
  switch (id) {
    case 'crates':
      return { title: 'Most crates off the quay', unit: 'crate' };
    case 'exports':
      return { title: 'Most rare motors to Halloran’s', unit: 'motor' };
    case 'sold-2':
      return { title: 'Most Prime goods sold', unit: 'unit' };
    case 'sold-1':
      return { title: 'Most Fine goods sold', unit: 'unit' };
    case 'sold-0':
      return { title: 'Most Standard goods sold', unit: 'unit' };
    case 'contests':
      return { title: 'Most contests won', unit: 'contest' };
    case 'jobs':
      return { title: 'Most firm’s jobs pulled off', unit: 'job' };
    case 'independent':
      return { title: 'Independent of the week', unit: '£' };
    default:
      return { title: id, unit: '' };
  }
}

export interface TallyRow {
  holder: string;
  stat: string;
  value: number;
  /** When it last changed (ties: whoever got there first). */
  at: number;
}

/**
 * The week's honours from its tallies (pure). One holder per honour: the highest figure, ties to whoever reached it
 * first, then the lower key. Independent of the week: the independent (`c<id>`) with the most business in pounds.
 * District leads are not tallies: the caller adds them from the territory at the week's end.
 */
export function pickHonours(rows: readonly TallyRow[]): { honour: HonourId; holder: string; value: number }[] {
  const best = (stat: string, only?: (h: string) => boolean): TallyRow | null => {
    let b: TallyRow | null = null;
    for (const r of rows) {
      if (r.stat !== stat || !(r.value > 0) || (only && !only(r.holder))) continue;
      if (!b || r.value > b.value || (r.value === b.value && (r.at < b.at || (r.at === b.at && r.holder < b.holder)))) b = r;
    }
    return b;
  };
  const out: { honour: HonourId; holder: string; value: number }[] = [];
  for (const [honour, stat] of [['crates', 'crates'], ['exports', 'exports'], ['sold-2', 'sold-2'], ['sold-1', 'sold-1'], ['sold-0', 'sold-0'], ['contests', 'contests'], ['jobs', 'jobs']] as const) {
    const r = best(stat);
    if (r) out.push({ honour, holder: r.holder, value: Math.round(r.value) });
  }
  const ind = best('value', (h) => h.startsWith('c'));
  if (ind) out.push({ honour: 'independent', holder: ind.holder, value: Math.round(ind.value) });
  return out;
}
